import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  LayoutDashboard, 
  Clock, 
  FileText, 
  Users, 
  User, 
  LogOut, 
  Menu,
  ChevronRight,
  ChevronDown,
  PawPrint,
  Megaphone,
  Building2,
  Award,
  Mail,
  UserCheck,
  Truck,
  Wrench,
  ClipboardCheck,
  CalendarOff,
  AlertTriangle,
  Droplets,
  Car,
  Hammer,
  HardHat,
  XCircle,
  MessageSquare,
  ClipboardList,
  Navigation,
  MapPin,
  DollarSign,
  Settings as SettingsIcon,
  ShieldAlert
} from "lucide-react";

import { SiteReminder } from './SiteReminder';
import { ClockOutConfirm } from './ClockOutConfirm';
export default function Layout({ children }) {
  const { user, logout, authAxios } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);
  const [openDropdowns, setOpenDropdowns] = useState({});

  const isAdmin = user?.role === 'admin';

  // Fetch pending approvals count and online count for admins
  useEffect(() => {
    if (isAdmin && authAxios) {
      const fetchCounts = async () => {
        try {
          const [pendingRes, onlineRes] = await Promise.all([
            authAxios.get('/pending-approvals/count'),
            authAxios.get('/location/admin/online-count')
          ]);
          setPendingCount(pendingRes.data.count);
          setOnlineCount(onlineRes.data.online_count);
        } catch (error) {
          console.error('Failed to fetch counts:', error);
        }
      };
      fetchCounts();
      const interval = setInterval(fetchCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, authAxios]);

  // Admin navigation structure with dropdowns
  const adminNavStructure = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard, type: 'link' },
    { name: 'Live Tracking', path: '/admin/live-tracking', icon: Navigation, type: 'link', badge: onlineCount > 0 ? onlineCount : null, badgeLabel: 'online' },
    { 
      name: 'Employees', 
      icon: Users, 
      type: 'dropdown',
      items: [
        { name: 'Records', path: '/admin/employee-records', icon: FileText },
        { name: 'Invites', path: '/admin/invites', icon: Mail },
        { name: 'Time Off Requests', path: '/admin/time-off', icon: CalendarOff },
        { name: 'Certifications', path: '/admin/certifications', icon: Award },
        { name: 'Announcements', path: '/admin/announcements', icon: Megaphone },
      ]
    },
    { 
      name: 'Equipment', 
      icon: Truck, 
      type: 'dropdown',
      items: [
        { name: 'Inventory', path: '/admin/equipment', icon: Wrench },
        { name: 'Inspections', path: '/admin/equipment-inspections', icon: ClipboardCheck },
        { name: 'Service Records', path: '/admin/service-records', icon: Clock },
      ]
    },
    { 
      name: 'Documents', 
      icon: FileText, 
      type: 'dropdown',
      items: [
        { name: 'All Documents', path: '/admin/documents', icon: FileText },
        { name: 'Incident Reports', path: '/admin/incident-reports', icon: ShieldAlert },
        { name: 'Service Requests', path: '/admin/service-requests', icon: Wrench },
        { name: 'Spill Reports', path: '/admin/spill-reports', icon: Droplets },
        { name: 'Vehicle Inspections', path: '/admin/vehicle-inspections', icon: Car },
        { name: 'Hand Tool Inspections', path: '/admin/hand-tool-inspections', icon: Hammer },
        { name: 'PPE Inspections', path: '/admin/ppe-inspections', icon: HardHat },
        { name: 'Toolbox Talks', path: '/admin/toolbox-talks', icon: MessageSquare },
        { name: 'FLRA', path: '/admin/flra', icon: ClipboardList },
        { name: 'Out of Service', path: '/admin/out-of-service', icon: XCircle },
      ]
    },
    { name: 'Projects', path: '/admin/projects', icon: Building2, type: 'link' },
    { name: 'Payroll', path: '/admin/payroll', icon: DollarSign, type: 'link' },
    { name: 'Approvals', path: '/admin/approvals', icon: UserCheck, type: 'link', badge: pendingCount },
  ];

  const employeeNavItems = [
    { name: 'Dashboard', path: '/dashboard', icon: Clock, type: 'link' },
    { name: 'My Records', path: '/my-records', icon: FileText, type: 'link' },
    {
      name: 'Forms',
      icon: ShieldAlert,
      type: 'dropdown',
      items: [
        { name: 'Incident Reports', path: '/incidents', icon: ShieldAlert },
        { name: 'Service Requests', path: '/service-requests', icon: Wrench },
        { name: 'Spill Reports', path: '/spill-reports', icon: Droplets },
        { name: 'Vehicle Inspections', path: '/vehicle-inspections', icon: Car },
        { name: 'Hand Tool Inspections', path: '/hand-tool-inspections', icon: Hammer },
        { name: 'PPE Inspections', path: '/ppe-inspections', icon: HardHat },
        { name: 'Toolbox Talks', path: '/toolbox-talks', icon: MessageSquare },
        { name: 'FLRA', path: '/flra', icon: ClipboardList },
        { name: 'Out of Service', path: '/out-of-service', icon: XCircle }
      ]
    },
    { name: 'Time Off', path: '/time-off', icon: CalendarOff, type: 'link' },
    { name: 'Announcements', path: '/announcements', icon: Megaphone, type: 'link' },
    { name: 'Profile', path: '/profile', icon: User, type: 'link' }
  ];

  const navStructure = isAdmin ? adminNavStructure : employeeNavItems;

  const initials = user?.full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase() || 'U';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActiveDropdown = (items) => {
    return items?.some(item => location.pathname === item.path);
  };

  const toggleMobileDropdown = (name) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Desktop Navigation with Dropdowns
  const DesktopNav = () => (
    <nav className="hidden lg:flex items-center gap-1">
      {navStructure.map((item) => {
        if (item.type === 'link') {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                isActive 
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20' 
                  : 'text-stone-600 hover:bg-stone-100'
              }`}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.name}</span>
              {item.badge > 0 && (
                <Badge className={`ml-1 text-white text-xs px-1.5 py-0 h-5 min-w-[20px] flex items-center justify-center ${
                  item.badgeLabel === 'online' ? 'bg-emerald-500 hover:bg-emerald-500' : 'bg-rose-500 hover:bg-rose-500'
                }`}>
                  {item.badge}
                </Badge>
              )}
            </Link>
          );
        }
        
        // Dropdown item
        const isDropdownActive = isActiveDropdown(item.items);
        return (
          <DropdownMenu key={item.name}>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className={`flex items-center gap-2 px-3 py-2 h-auto text-sm font-medium whitespace-nowrap ${
                  isDropdownActive 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
                data-testid={`nav-dropdown-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.name}</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {item.items.map((subItem) => {
                const isSubActive = location.pathname === subItem.path;
                return (
                  <DropdownMenuItem key={subItem.path} asChild>
                    <Link 
                      to={subItem.path} 
                      className={`flex items-center gap-2 cursor-pointer ${isSubActive ? 'bg-emerald-50 text-emerald-700' : ''}`}
                      data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <subItem.icon className="w-4 h-4" />
                      {subItem.name}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </nav>
  );

  // Mobile Navigation with Collapsibles
  const MobileNav = () => (
    <nav className="flex flex-col gap-1 px-4">
      {navStructure.map((item) => {
        if (item.type === 'link') {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive 
                  ? 'bg-emerald-600 text-white' 
                  : 'text-emerald-100 hover:bg-emerald-800/50'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.name}</span>
              {item.badge > 0 && (
                <Badge className="ml-auto bg-rose-500 hover:bg-rose-500 text-white text-xs px-1.5 py-0 h-5 min-w-[20px] flex items-center justify-center">
                  {item.badge}
                </Badge>
              )}
            </Link>
          );
        }

        // Dropdown as collapsible for mobile
        const isDropdownActive = isActiveDropdown(item.items);
        const isOpen = openDropdowns[item.name] || isDropdownActive;
        
        return (
          <Collapsible key={item.name} open={isOpen} onOpenChange={() => toggleMobileDropdown(item.name)}>
            <CollapsibleTrigger asChild>
              <button 
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 w-full ${
                  isDropdownActive 
                    ? 'bg-emerald-800/50 text-white' 
                    : 'text-emerald-100 hover:bg-emerald-800/50'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pl-6 mt-1 space-y-1">
                {item.items.map((subItem) => {
                  const isSubActive = location.pathname === subItem.path;
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-all duration-200 ${
                        isSubActive 
                          ? 'bg-emerald-600 text-white' 
                          : 'text-emerald-200 hover:bg-emerald-800/50'
                      }`}
                    >
                      <subItem.icon className="w-4 h-4" />
                      <span>{subItem.name}</span>
                    </Link>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </nav>
  );

  const Sidebar = () => (
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
          <PawPrint className="w-7 h-7 text-white" />
        </div>
        <div>
          <h2 className="font-bold text-lg text-emerald-50 font-heading">Lil Bear Tracks</h2>
          <p className="text-xs text-emerald-300 capitalize">{user?.role} Portal</p>
        </div>
      </div>
      <MobileNav />
      <div className="mt-auto pt-6 border-t border-emerald-800">
        <div className="flex items-center gap-3 px-4">
          <Avatar className="w-10 h-10 border-2 border-emerald-600">
            <AvatarFallback className="bg-emerald-700 text-emerald-100 text-sm font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-50 truncate">{user?.full_name}</p>
            <p className="text-xs text-emerald-400 truncate">{user?.email}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Mobile Menu */}
            <div className="flex items-center gap-4">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-0 bg-emerald-950 border-emerald-900">
                  <Sidebar />
                </SheetContent>
              </Sheet>

              <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-600/20">
                  <PawPrint className="w-6 h-6 text-white" />
                </div>
                <div className="hidden sm:block whitespace-nowrap">
                  <h1 className="font-bold text-stone-900 leading-tight font-heading">Lil Bear Tracks</h1>
                  <p className="text-xs text-stone-500 capitalize">{user?.role} Portal</p>
                </div>
              </Link>
            </div>

            {/* Desktop Nav */}
            <DesktopNav />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-3 px-3" data-testid="user-menu-btn">
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-emerald-600 text-white text-sm font-medium">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-stone-900">{user?.full_name}</p>
                    <p className="text-xs text-stone-500 capitalize">{user?.role}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-stone-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                {!isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer" data-testid="profile-menu-item">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer" data-testid="settings-menu-item">
                    <SettingsIcon className="w-4 h-4 mr-2" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-rose-600 cursor-pointer"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <SiteReminder />
      <ClockOutConfirm />
      <main className="animate-fade-in">
        {children}
      </main>
    </div>
  );
}
